import { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";

import ActionBar from "../../../components/Shared/ActionBar"
import AdminLayout from "../../../components/Shared/AdminLayout"
import Container from "../../../components/Shared/Container"
import BrandCard from "../../../components/Admin/Brands/BrandCard"
import BrandList from "../../../components/Admin/Brands/BrandList"
import Pagination from "../../../components/Shared/Pagination"
import BrandViewModal from "../../../components/Admin/Brands/BrandViewModal"

import * as adminHandlers from "../redux/handlers"


function Brands(props) {
    const dispatch = useDispatch();
    const { brands, brandsTotal, loading, error } = useSelector(state => state.admin);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(100);
    const [selectedBrand, setSelectedBrand] = useState(null);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchDebounce, setSearchDebounce] = useState("");
    const [viewMode, setViewMode] = useState("list");

    useEffect(() => {
        dispatch(adminHandlers.fetchBrandsHandler({ 
            page: currentPage, 
            size: itemsPerPage,
            search: searchDebounce 
        }));
    }, [dispatch, currentPage, itemsPerPage, searchDebounce])

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchDebounce(searchQuery);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Handle view
    const handleView = (brand) => {
        setSelectedBrand(brand);
        // Delay opening to allow modal to mount first
        setTimeout(() => {
            setIsViewModalOpen(true);
        }, 10);
    };

    // Handle page change
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    // Handle search
    const handleSearch = useCallback((query) => {
        setSearchQuery(query);
        setCurrentPage(1);
    }, []);

    console.log(brands.length);

    // Calculate total pages
    const totalPages = Math.ceil(brandsTotal / itemsPerPage) || 1;

    const viewToggle = (
        <div className="flex items-center bg-charleston-green border border-zinc-700 rounded-full p-1">
            <button
                onClick={() => setViewMode("list")}
                aria-label="List view"
                className={`p-2 rounded-full transition-colors ${
                    viewMode === "list"
                        ? "bg-zinc-700 text-white"
                        : "text-zinc-500 hover:text-white"
                }`}
            >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
            </button>
            <button
                onClick={() => setViewMode("card")}
                aria-label="Card view"
                className={`p-2 rounded-full transition-colors ${
                    viewMode === "card"
                        ? "bg-zinc-700 text-white"
                        : "text-zinc-500 hover:text-white"
                }`}
            >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h3a2 2 0 012 2v3a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM13 6a2 2 0 012-2h3a2 2 0 012 2v3a2 2 0 01-2 2h-3a2 2 0 01-2-2V6zM4 15a2 2 0 012-2h3a2 2 0 012 2v3a2 2 0 01-2 2H6a2 2 0 01-2-2v-3zM13 15a2 2 0 012-2h3a2 2 0 012 2v3a2 2 0 01-2 2h-3a2 2 0 01-2-2v-3z" />
                </svg>
            </button>
        </div>
    );

    return (
        <AdminLayout>
            <ActionBar
                title="Brands Overview"
                showSearch={true}
                searchPlaceholder="Search"
                onSearch={handleSearch}
                additionalActions={viewToggle}
            />
            <Container>
                <div className="h-full flex flex-col justify-between">
                    {loading ? (
                        <div className="flex justify-center items-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
                        </div>
                    ) : error ? (
                        <div className="text-red-500">Error: {error.data?.message || "Failed to load brands"}</div>
                    ) : (
                        <>
                    {viewMode === "card" ? (
                        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-6 content-start">
                            {brands.map((item) => (
                                <BrandCard
                                key={item.id}
                                data={item}
                                onView={handleView}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="mb-6">
                            <BrandList data={brands} onView={handleView} />
                        </div>
                    )}
                    {brands.length > 0 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalItems={brandsTotal}
                            itemsPerPage={itemsPerPage}
                            onPageChange={handlePageChange}
                        />
                    )}
                    {searchQuery && brands.length === 0 && (
                        <div className="text-start">
                            <p className="text-zinc-400">No brands found matching "{searchQuery}"</p>
                        </div>
                    )}
                        </>
                    )}
                </div>
            </Container>
            
            {/* Brand View Modal */}
            {selectedBrand && (
                <BrandViewModal
                    brand={selectedBrand}
                    isOpen={isViewModalOpen}
                    onClose={() => {
                        setIsViewModalOpen(false);
                        // Delay clearing selectedBrand to allow animation
                        setTimeout(() => setSelectedBrand(null), 300);
                    }}
                    onUpdate={(updatedBrand) => setSelectedBrand(updatedBrand)}
                />
            )}
        </AdminLayout>
    )
}

export default Brands
